<script setup lang="ts">
import { z } from 'zod'
import { idValidation, requiredError } from '~/utils/zodUtils'
import {
  type AddDonationParams,
  RecurringDonationPeriodicTypeEnum,
  RecurringDonationPeriodicTypeOptions
} from '~/types/donations'
import { useTypedForm } from '~/composables/useTypedForm'
import { recurringDonationsPeriodicTypeOptions } from '~/models/donations'
import { useDonationsStore } from '~/stores/donations'
import { defu } from 'defu'
import type { UIProp } from '~/types/elementsUi'
import type { UserId } from '#shared/types/users'
import type { DonationBoxId, DonationFormAmountOptions } from '#shared/types/donations'
import type { IDealPaymentStatus } from '~/types/shared'

interface DonationFormProps {
  type: 'page' | 'box'
  slug?: string
  donationBoxId?: DonationBoxId
  disabled?: boolean
  donationAmounts?: Partial<DonationFormAmountOptions>

  ui?: UIProp<'container' | 'header' | 'content' | 'footer'>
}

const props = defineProps<DonationFormProps>()

const paymentForm = useTemplateRef('payment-form')

const userStore = useUsersStore()
const donationStore = useDonationsStore()

const { t, te } = useI18n()
const donationFormBaseKeyPath = 'components.organisms.donation.donation-form'
const addBalanceFormBaseKeyPath = 'components.ideal-payment-status'

const overrideUser = ref(false)

const paymentUrl = ref<string>()
const paymentStatus = ref<IDealPaymentStatus>()

const computedUi = computed(() =>
  nuxtUiMerge(props.ui, {
    container: tw`max-md:px-2`,
    header: tw`max-md:px-4`,
    footer: 'pt-0!'
  })
)

const user = computed(() => userStore.specificUser)
const userIdentifier = computed(() => {
  if (userStore.specificUser) {
    return userStore.specificUser.id.toString()
  } else {
    return donationStore.getDonationUUID()
  }
})

const { setChannel, startListening } = useWebSocket({
  channel: `tg-website-channel-ideal-${userIdentifier.value}`,
  event: 'IDealPaymentUpdate',
  callback: (message: { status: IDealPaymentStatus; identifier: UserId | string }) => {
    if (message.identifier != userIdentifier.value) {
      return
    }

    paymentStatus.value = message.status

    if (['success', 'post-payment-error'].includes(paymentStatus.value)) {
      donationStore.clearDonationUUID()
    }
  },
  isPublic: true
})
watch(userIdentifier, (newValue) => {
  setChannel(`tg-website-channel-ideal-${newValue}`)
  startListening()
})

const { formProps, useField, handleSubmit, resetForm, errors, isLoading } =
  useTypedForm<AddDonationParams>({
    id: 'donation-form',
    schema: {
      amount: z.number(requiredError(t('error-messages.amount-is-required'))).min(
        1,
        t('error-messages.minimum-amount-price', {
          n: formatCurrency(1)
        })
      ),
      periodicType: RecurringDonationPeriodicTypeOptions,
      slug: z.string().optional(),
      userId: idValidation<UserId>().nullable(),
      donationBoxId: idValidation<DonationBoxId>().nullable(),
      isCorporate: z.boolean(),
      donor: z.object({
        firstname: z.string().trim().min(1),
        lastname: z.string().trim().min(1),
        email: z.email()
      })
    },
    initialValues: {
      amount: props.type === 'page' ? 10 : undefined,
      periodicType:
        props.type === 'box'
          ? RecurringDonationPeriodicTypeEnum.OneTime
          : RecurringDonationPeriodicTypeEnum.Monthly,
      isCorporate: false,
      slug: props.slug,
      userId: null,
      donationBoxId: props.donationBoxId ?? null
    }
  })

const { value: periodicType } = useField('periodicType')
const { value: amount } = useField('amount')

watch(periodicType, () => paymentForm.value?.resetFields())

const fieldInputs = computed<DonationFormAmountOptions>(() => {
  let donationAmounts
  if (periodicType.value === RecurringDonationPeriodicTypeEnum.OneTime) {
    if (props.type === 'box') {
      donationAmounts = {
        low: 10,
        medium: 25,
        high: 40
      }
    } else {
      donationAmounts = {
        low: 20,
        medium: 50,
        high: 500
      }
    }
  } else {
    donationAmounts = {
      low: 20,
      medium: 10,
      high: 8
    }
  }

  return defu(props.donationAmounts, donationAmounts)
})

const paymentAmountText = computed(() => {
  const textPath = `${donationFormBaseKeyPath}.amount-description.${periodicType.value}.${amount.value}`

  return te(textPath) ? t(textPath) : undefined
})

watch(
  overrideUser,
  (newValue) => {
    if (newValue) {
      resetForm({
        values: {
          amount: amount.value,
          periodicType:
            props.type === 'box'
              ? RecurringDonationPeriodicTypeEnum.OneTime
              : RecurringDonationPeriodicTypeEnum.Monthly,
          slug: props.slug,
          userId: null,
          donationBoxId: props.donationBoxId ?? null,
          donor: undefined
        }
      })
    } else if (user.value) {
      resetForm({
        values: {
          amount: amount.value,
          periodicType:
            props.type === 'box'
              ? RecurringDonationPeriodicTypeEnum.OneTime
              : RecurringDonationPeriodicTypeEnum.Monthly,
          slug: props.slug,
          userId: user.value.id,
          donationBoxId: props.donationBoxId ?? null,
          donor: {
            firstname: user.value.userProfile.firstname,
            lastname: user.value.userProfile.lastname,
            email: user.value.email
          }
        }
      })
    }
  },
  { immediate: true }
)

const onSubmit = handleSubmit(async (values) => {
  const windowOpen = window.open('', '_blank')

  const result = await donationStore.createDonation({
    ...values,
    identifier: userIdentifier.value
  })

  if (result && windowOpen) {
    windowOpen.location.href = result
    paymentUrl.value = result
  } else {
    windowOpen?.close()
  }
})

const resetPaymentStatus = () => {
  paymentStatus.value = undefined
}
</script>

<template>
  <VCard
    class="lg:ml-3!"
    :ui="computedUi"
    no-box-shadow
    :title="!paymentStatus ? `${donationFormBaseKeyPath}.title` : undefined"
  >
    <template #content>
      <FormWrapper v-slot="{ k }" v-bind="formProps" @submit="onSubmit">
        <template v-if="!paymentStatus">
          <div v-show="type === 'page'" class="column full">
            <FormRadio
              :label="`${donationFormBaseKeyPath}.labels.periodic-type`"
              :name="k.periodicType"
              :options="recurringDonationsPeriodicTypeOptions"
              :disabled="disabled"
            />
          </div>

          <div class="column full">
            <FormPayment
              ref="payment-form"
              :title="`${donationFormBaseKeyPath}.labels.donation-amount`"
              :name="k.amount"
              :error-message="errors.amount"
              :field-inputs="fieldInputs"
              :disabled="disabled"
              hide-euro
            />

            <p v-if="paymentAmountText">
              {{ paymentAmountText }}
            </p>
          </div>

          <FormInput label="Slug" :name="k.slug" hidden />
          <FormInput label="User ID" :name="k.userId" hidden />
          <FormInput label="Donation Box ID" :name="k.donationBoxId" hidden />

          <template v-if="user && !overrideUser">
            <div class="column pt-0 full">
              <VCard :ui="{ content: 'prose inline-block' }">
                <template #content>
                  <VI18n
                    :key-path="`${donationFormBaseKeyPath}.user-info.name`"
                    class="mb-2! text-xl"
                  >
                    <template #name>
                      <span class="font-bold">
                        {{ user.userProfile.firstname }}
                        {{ user.userProfile.lastname }}
                      </span>
                    </template>
                  </VI18n>

                  <VI18n
                    :key-path="`${donationFormBaseKeyPath}.user-info.phone`"
                    class="my-0! text-left"
                  >
                    <template #icon>
                      <VIcon icon="fa-phone" class="pr-1" />
                    </template>
                    <template #phone>
                      {{ user.userProfile.phone }}
                    </template>
                  </VI18n>

                  <VI18n
                    :key-path="`${donationFormBaseKeyPath}.user-info.email`"
                    class="my-0! text-left"
                  >
                    <template #icon>
                      <VIcon icon="fa-mail-bulk" class="pr-1" />
                    </template>
                    <template #email>
                      {{ user.email }}
                    </template>
                  </VI18n>

                  <VI18n
                    :key-path="`${donationFormBaseKeyPath}.user-info.city`"
                    class="my-0! text-left"
                  >
                    <template #icon>
                      <VIcon icon="fa-city" class="pr-1!" />
                    </template>
                    <template #city>
                      {{ user.userProfile.city }}
                    </template>
                  </VI18n>

                  <VField class="mt-4!" :ui="{ control: 'w-full' }">
                    <VCheckbox
                      v-model="overrideUser"
                      value="override-user"
                      :label="`${donationFormBaseKeyPath}.labels.override-user`"
                      :disabled="disabled"
                    />
                  </VField>
                </template>
              </VCard>
            </div>
          </template>

          <div v-show="!user || overrideUser" class="field column full">
            <fieldset class="m-0 columns multiline">
              <legend class="field-label column pt-0 pb-1 full">
                {{ t(`${donationFormBaseKeyPath}.labels.details`) }}
              </legend>
              <div class="column py-0 full">
                <FormInput
                  :name="k['donor.firstname']"
                  label="labels.first-name"
                  :disabled="disabled"
                />
              </div>
              <div class="column pb-0 full">
                <FormInput
                  :name="k['donor.lastname']"
                  label="labels.last-name"
                  :disabled="disabled"
                />
              </div>
              <div class="column pb-0 full">
                <FormInput
                  :name="k['donor.email']"
                  label="labels.email"
                  type="email"
                  input-mode="email"
                  :disabled="disabled"
                />
                <p>{{ t(`${donationFormBaseKeyPath}.labels.email-text`) }}</p>
              </div>
            </fieldset>
          </div>

          <VField
            v-if="user && overrideUser"
            class="mt-4! column full"
            :ui="{ control: 'w-full' }"
          >
            <VCheckbox
              v-model="overrideUser"
              value="override-user"
              :label="`${donationFormBaseKeyPath}.labels.override-user`"
              :disabled="disabled"
            />
          </VField>

          <FormCheckbox
            :name="k.isCorporate"
            value="is-corporate"
            :disabled="disabled"
            :ui="{
              field: 'column full pt-0!'
            }"
          >
            {{ t(`${donationFormBaseKeyPath}.labels.is-corporate`) }}
          </FormCheckbox>
        </template>
        <template v-else-if="paymentStatus === 'pending'">
          <div class="flex flex-col items-center justify-center">
            <VIcon icon="fa-spinner fa-pulse" color="info" class="mt-4 mb-12 text-8xl" />
            <h1 class="title mb-2! text-center text-title">
              {{ t(`${addBalanceFormBaseKeyPath}.pending.title`) }}
            </h1>
            <p class="text-center">
              {{ t(`${addBalanceFormBaseKeyPath}.pending.description`) }}
            </p>

            <p v-if="paymentUrl" class="mt-4 text-center">
              {{ t(`${addBalanceFormBaseKeyPath}.pending.payment-url-description`) }}
            </p>
          </div>
        </template>
        <template v-else-if="paymentStatus === 'success'">
          <div class="flex flex-col items-center justify-center">
            <VIcon icon="fa-check-circle" color="success" class="mt-4 mb-12 text-8xl" />
            <h1 class="title mb-2! text-center text-title">
              {{ t(`${addBalanceFormBaseKeyPath}.success.title`) }}
            </h1>
            <p class="text-center">
              {{ t(`${addBalanceFormBaseKeyPath}.success.description`) }}
            </p>
          </div>
        </template>
        <template v-else-if="paymentStatus === 'cancelled'">
          <div class="flex flex-col items-center justify-center">
            <VIcon icon="fa-ban" color="black" class="mt-4 mb-12 text-8xl" />
            <h1 class="title mb-2! text-center text-title">
              {{ t(`${addBalanceFormBaseKeyPath}.cancelled.title`) }}
            </h1>
            <p class="text-center">
              {{ t(`${addBalanceFormBaseKeyPath}.cancelled.description`) }}
            </p>
          </div>
        </template>
        <template v-else-if="paymentStatus === 'pre-payment-error'">
          <div class="flex flex-col items-center justify-center">
            <VIcon
              icon="fa-exclamation-triangle"
              color="warning"
              class="mt-4 mb-12 text-8xl"
            />
            <h1 class="title mb-2! text-center text-title">
              {{ t(`${addBalanceFormBaseKeyPath}.pre-payment-error.title`) }}
            </h1>
            <p class="text-center">
              {{ t(`${addBalanceFormBaseKeyPath}.pre-payment-error.description`) }}
            </p>
          </div>
        </template>
        <template v-else-if="paymentStatus === 'post-payment-error'">
          <div class="flex flex-col items-center justify-center">
            <VIcon
              icon="fa-exclamation-triangle"
              color="danger"
              class="mt-4 mb-12 text-8xl"
            />
            <h1 class="title mb-2! text-center text-title">
              {{ t(`${addBalanceFormBaseKeyPath}.post-payment-error.title`) }}
            </h1>
            <p class="text-center">
              {{ t(`${addBalanceFormBaseKeyPath}.post-payment-error.description`) }}
            </p>
          </div>
        </template>
      </FormWrapper>
    </template>
    <template #footer>
      <VButton
        v-if="!paymentStatus"
        type="submit"
        :form="formProps.id"
        width="full"
        :disabled="disabled"
        :loading="isLoading"
      >
        {{ t(`${donationFormBaseKeyPath}.button`) }}
      </VButton>
      <VButton
        v-if="paymentStatus === 'pending' && paymentUrl"
        class="mt-4"
        width="full"
        :to="paymentUrl"
        color="info"
        icon-right="fa-arrow-right"
        target="_blank"
        rel="noopener noreferrer"
        >{{ t(`${addBalanceFormBaseKeyPath}.pending.payment-url-button`) }}</VButton
      >
      <VButton
        v-if="paymentStatus === 'pre-payment-error' || paymentStatus === 'cancelled'"
        width="full"
        icon-left="fa-arrow-left"
        @click="resetPaymentStatus"
      >
        {{ t(`${addBalanceFormBaseKeyPath}.try-again`) }}
      </VButton>
      <VButton
        v-if="
          paymentStatus === 'pre-payment-error' || paymentStatus === 'post-payment-error'
        "
        :to="{ name: 'vragen' }"
        width="full"
        :color="paymentStatus === 'pre-payment-error' ? undefined : 'danger'"
      >
        {{ t(`${addBalanceFormBaseKeyPath}.error-contact-us`) }}
      </VButton>
    </template>
  </VCard>
</template>

<style scoped></style>
